import './EconomicsView.css';

function parseEconomics(json) {
  if (!json) {
    return null;
  }

  if (typeof json !== 'string') {
    return json;
  }

  try {
    return JSON.parse(json);
  } catch (error) {
    return json;
  }
}

function humanizeKey(key, fieldLabelMap) {
  if (fieldLabelMap && fieldLabelMap[key]) {
    return fieldLabelMap[key];
  }

  return String(key)
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function isMoneyShape(value) {
  return (
    isPlainObject(value) &&
    Object.keys(value).length === 2 &&
    Object.prototype.hasOwnProperty.call(value, 'amount') &&
    Object.prototype.hasOwnProperty.call(value, 'currency')
  );
}

function isObjectArray(value) {
  return Array.isArray(value) && value.length > 0 && isPlainObject(value[0]);
}

function isPrimitiveArray(value) {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every((item) => !isPlainObject(item) && !Array.isArray(item))
  );
}

function isDateLike(item) {
  return (
    typeof item === 'string' &&
    /^\d{4}-\d{2}-\d{2}(T[\d:.+-]*)?$/.test(item.trim())
  );
}

// A list of dates (e.g. barrier / fixing observation dates) reads far better as a
// numbered table than as a comma-joined string. Other short primitive arrays
// (e.g. underlyings) stay inline.
function isDateArray(value) {
  return isPrimitiveArray(value) && value.every(isDateLike);
}

const ENVELOPE_METADATA_KEYS = new Set([
  'tenantId',
  'tenant_id',
  'clientId',
  'client_id',
  'capturedAt',
  'captured_at',
  'productCode',
  'product_code',
  'productType',
  'product_type',
  'schemaVersion',
  'schema_version',
  'structureVersionId',
  'structure_version_id',
]);

function stripEnvelopeMetadata(data) {
  return Object.keys(data).reduce((result, key) => {
    if (!ENVELOPE_METADATA_KEYS.has(key)) {
      result[key] = data[key];
    }

    return result;
  }, {});
}

function getRenderableEconomicsData(data) {
  if (isPlainObject(data.fields)) {
    return data.fields;
  }

  return stripEnvelopeMetadata(data);
}

// PB-1945: captured economics carry doubles straight from the booking engine,
// so IEEE-754 artifacts reach the summary verbatim — a 105 strike renders as
// 105.00000000000001. A double holds at most 15 significant digits without
// exposing them, so the round-trip drops the artifact and keeps every real one.
const SIGNIFICANT_DIGITS = 15;

// Intl's ceiling. Deliberately not a tighter cap: the artifact is already gone
// by this point, and capping low would round a small rate (1e-9) away to "0".
const MAX_FRACTION_DIGITS = 20;

const numberFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: MAX_FRACTION_DIGITS,
});

function formatNumber(value) {
  if (!Number.isFinite(value)) {
    return String(value);
  }

  return numberFormatter.format(
    Number.parseFloat(value.toPrecision(SIGNIFICANT_DIGITS))
  );
}

// A money amount arrives as either a double or a BigDecimal serialized to a
// string; anything that is not actually numeric is left exactly as it came.
function formatMoneyAmount(amount) {
  if (typeof amount === 'number') {
    return formatNumber(amount);
  }

  if (typeof amount === 'string' && amount.trim() !== '') {
    const parsed = Number(amount);
    return Number.isFinite(parsed) ? formatNumber(parsed) : amount;
  }

  return stringifyValue(amount);
}

function stringifyValue(value) {
  if (value === undefined || value === null) {
    return '';
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  // PB-1945: only real numbers are formatted. A numeric-looking STRING is left
  // alone — reference numbers and ids would otherwise pick up thousands
  // separators and stop matching what the desk searches for.
  if (typeof value === 'number') {
    return formatNumber(value);
  }

  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch (error) {
      return String(value);
    }
  }

  return value;
}

function renderValue(value) {
  if (isMoneyShape(value)) {
    return `${value.currency} ${formatMoneyAmount(value.amount)}`;
  }

  if (typeof value === 'boolean' || typeof value === 'number') {
    return stringifyValue(value);
  }

  if (Array.isArray(value)) {
    return value.map((item) => stringifyValue(item)).join(', ');
  }

  return value;
}

function getValueClassName(value) {
  if (typeof value === 'number') {
    return 'economics-value economics-value--numeric';
  }

  if (isMoneyShape(value)) {
    return 'economics-value economics-value--numeric';
  }

  return 'economics-value';
}

function renderScalarRows(obj, fieldLabelMap, depth = 0, path = '') {
  if (!isPlainObject(obj)) {
    return [];
  }

  return Object.keys(obj).flatMap((key) => {
    const value = obj[key];
    const rowKey = path ? `${path}.${key}` : key;
    const label = humanizeKey(key, fieldLabelMap);

    if (depth >= 5) {
      return (
        <div className="economics-row" key={rowKey}>
          <div
            className="economics-label"
            style={{ marginLeft: `${depth * 16}px` }}
          >
            {label}
          </div>
          <div className="economics-value">{stringifyValue(value)}</div>
        </div>
      );
    }

    if (isObjectArray(value) || isDateArray(value)) {
      return [];
    }

    if (isPlainObject(value) && !isMoneyShape(value)) {
      return [
        <div className="economics-section-row" key={`${rowKey}:header`}>
          <div className="economics-section-title">{label}:</div>
        </div>,
        ...renderScalarRows(value, fieldLabelMap, depth + 1, rowKey),
      ];
    }

    return (
      <div className="economics-row" key={rowKey}>
        <div
          className="economics-label"
          style={{ marginLeft: `${depth * 16}px` }}
        >
          {label}
        </div>
        <div className={getValueClassName(value)}>{renderValue(value)}</div>
      </div>
    );
  });
}

function renderTables(obj, fieldLabelMap, depth = 0, path = '') {
  if (!isPlainObject(obj) || depth > 5) {
    return [];
  }

  return Object.keys(obj).flatMap((key) => {
    const value = obj[key];
    const tableKey = path ? `${path}.${key}` : key;

    if (isObjectArray(value)) {
      const columns = Object.keys(value[0]);
      const titleParts = tableKey
        .split('.')
        .map((part) => humanizeKey(part, fieldLabelMap));
      const title = titleParts.join(' ');

      return (
        <section className="economics-table-card" key={tableKey}>
          <div className="economics-table-header">
            <h6 className="economics-table-title">{title}</h6>
          </div>
          <div className="economics-table-scroll">
            <table className="table table-bordered table-sm table-hover economics-table mb-0">
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th key={column}>{humanizeKey(column, fieldLabelMap)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {value.map((row, rowIndex) => (
                  <tr key={`${tableKey}.${rowIndex}`}>
                    {columns.map((column) => (
                      <td key={column}>{stringifyValue(row[column])}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      );
    }

    if (isDateArray(value)) {
      const titleParts = tableKey
        .split('.')
        .map((part) => humanizeKey(part, fieldLabelMap));
      const title = titleParts.join(' ');

      return (
        <section className="economics-table-card" key={tableKey}>
          <div className="economics-table-header">
            <h6 className="economics-table-title">{title}</h6>
          </div>
          <div className="economics-table-scroll">
            <table className="table table-bordered table-sm table-hover economics-table mb-0">
              <thead>
                <tr>
                  <th style={{ width: '3rem' }}>#</th>
                  <th>{humanizeKey(key, fieldLabelMap)}</th>
                </tr>
              </thead>
              <tbody>
                {value.map((item, rowIndex) => (
                  <tr key={`${tableKey}.${rowIndex}`}>
                    <td>{rowIndex + 1}</td>
                    <td>{stringifyValue(item)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      );
    }

    if (isPlainObject(value) && !isMoneyShape(value)) {
      return renderTables(value, fieldLabelMap, depth + 1, tableKey);
    }

    return [];
  });
}

export default function EconomicsView({ json, fieldLabelMap, view = 'both' }) {
  const data = parseEconomics(json);

  if (!isPlainObject(data)) {
    return null;
  }

  const renderableData = getRenderableEconomicsData(data);
  const scalarRows =
    view !== 'tables' ? renderScalarRows(renderableData, fieldLabelMap) : [];
  const tableSections =
    view !== 'scalars' ? renderTables(renderableData, fieldLabelMap) : [];

  return (
    <div className="nk-block economics-view">
      {scalarRows.length > 0 && (
        <section className="economics-panel">
          <div className="economics-panel-header">
            <h6 className="economics-panel-title">Fields</h6>
          </div>
          <div className="economics-field-list">{scalarRows}</div>
        </section>
      )}
      {tableSections}
    </div>
  );
}
